import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

export type TimeValue = {
  unit: "seconds" | "minutes" | "hours" | "days";
  value: string;
};

export type AssetMetadata = {
  metadata_name: string;
  metadata_value: TimeValue;
};

type Params = {
  assetType: "zap";
  metadataName: "time_saved";
  zapId: string;
};

function getDefaultValue(): AssetMetadata {
  return {
    metadata_name: "time_saved",
    metadata_value: {
      value: "0",
      unit: "minutes",
    },
  };
}

// Getter atom family for initial data
const zapAssetMetadataAtomFamily_SUSPENDS = atomFamily(
  (params: Params) => {
    const { metadataName, assetType, zapId } = params;

    const fetchAtom = atom(async () => {
      if (!zapId) {
        return getDefaultValue();
      }
      const res = await fetch(
        `/api/canvas/v1/asset-metadata/?asset_type=${assetType}&asset_id=${zapId}&metadata_name=${metadataName}`,
      );
      if (!res.ok) {
        return getDefaultValue();
      }
      const json = await res.json();
      return json.results[0] as AssetMetadata;
    });
    fetchAtom.debugLabel = `zapAssetMetadataAtomFamily_SUSPENDS(${zapId})`;
    return fetchAtom;
  },
  (a: Params, b: Params) =>
    a.zapId === b.zapId &&
    a.assetType === b.assetType &&
    a.metadataName === b.metadataName,
);

// Atom for current input value per zap
export const currentTimeSavedAtomFamily = atomFamily((zapId: string) => {
  const baseAtom = atom<TimeValue>({
    value: "0",
    unit: "minutes",
  });
  baseAtom.debugLabel = `currentTimeSavedAtomFamily(${zapId})`;
  return baseAtom;
});

// Add a new atom to track if the value has been modified per zap
export const hasTimeSavedBeenModifiedAtomFamily = atomFamily(
  (zapId: string) => {
    const baseAtom = atom<boolean>(false);
    baseAtom.debugLabel = `hasTimeSavedBeenModifiedAtomFamily(${zapId})`;
    return baseAtom;
  },
);

// Derived atom family that combines both
export const latestTimeSavedAtomFamily_SUSPENDS = atomFamily(
  (zapId: string) => {
    const asyncAtom = atom<Promise<TimeValue>>(async (get) => {
      const asyncValue = await get(
        zapAssetMetadataAtomFamily_SUSPENDS({
          zapId,
          assetType: "zap",
          metadataName: "time_saved",
        }),
      );

      return (
        asyncValue?.metadata_value || get(currentTimeSavedAtomFamily(zapId))
      );
    });
    asyncAtom.debugLabel = `latestTimeSavedAtomFamily_asyncAtom(${zapId})`;

    const baseAtom = atom((get) => {
      const hasBeenModified = get(hasTimeSavedBeenModifiedAtomFamily(zapId));

      if (hasBeenModified) {
        return get(currentTimeSavedAtomFamily(zapId));
      }

      return get(asyncAtom);
    });
    baseAtom.debugLabel = `latestTimeSavedAtomFamily_baseAtom(${zapId})`;

    return baseAtom;
  },
);
